const PLACEHOLDER_BASE = 'https://placehold.co'

function buildPlaceholder(width, height, label) {
  const text = encodeURIComponent(label)
  return `${PLACEHOLDER_BASE}/${width}x${height}/e8edf2/64748b/png?text=${text}`
}

export const EMAIL_IMAGE_PLACEHOLDER_LOGO_SIZE = { width: 160, height: 48 }
export const EMAIL_IMAGE_PLACEHOLDER_LOGO = buildPlaceholder(
  EMAIL_IMAGE_PLACEHOLDER_LOGO_SIZE.width,
  EMAIL_IMAGE_PLACEHOLDER_LOGO_SIZE.height,
  'Your logo'
)
export const EMAIL_IMAGE_PLACEHOLDER_HERO = buildPlaceholder(600, 340, 'Hero image')
export const EMAIL_IMAGE_PLACEHOLDER_HERO_GIF = buildPlaceholder(600, 340, 'Hero slideshow')
export const EMAIL_IMAGE_PLACEHOLDER_CARD = buildPlaceholder(280, 180, 'Add image')
export const EMAIL_IMAGE_PLACEHOLDER_SQUARE = buildPlaceholder(160, 160, 'Add image')
export const EMAIL_IMAGE_PLACEHOLDER_AVATAR = buildPlaceholder(128, 128, 'Guest photo')
export const EMAIL_IMAGE_PLACEHOLDER_LIFESTYLE = buildPlaceholder(600, 260, 'Lifestyle image')
export const EMAIL_IMAGE_PLACEHOLDER_EDITORIAL_HERO = buildPlaceholder(680, 380, 'Hero image')
export const EMAIL_IMAGE_PLACEHOLDER_EDITORIAL_STORY = buildPlaceholder(440, 300, 'Story image')

export const EMAIL_IMAGE_FIELD_PLACEHOLDERS = {
  logoUrl: EMAIL_IMAGE_PLACEHOLDER_LOGO,
  heroImgUrl: EMAIL_IMAGE_PLACEHOLDER_HERO,
  heroImg2: EMAIL_IMAGE_PLACEHOLDER_HERO,
  heroImg3: EMAIL_IMAGE_PLACEHOLDER_HERO,
  heroImg4: EMAIL_IMAGE_PLACEHOLDER_HERO,
  heroAnimatedImgUrl: EMAIL_IMAGE_PLACEHOLDER_HERO_GIF,
  col1Image: EMAIL_IMAGE_PLACEHOLDER_EDITORIAL_STORY,
  col2Image: EMAIL_IMAGE_PLACEHOLDER_EDITORIAL_STORY,
  col3Image: EMAIL_IMAGE_PLACEHOLDER_EDITORIAL_STORY,
  gallery1Image: EMAIL_IMAGE_PLACEHOLDER_CARD,
  gallery2Image: EMAIL_IMAGE_PLACEHOLDER_CARD,
  gallery3Image: EMAIL_IMAGE_PLACEHOLDER_CARD,
  gallery4Image: EMAIL_IMAGE_PLACEHOLDER_CARD,
  lifestyleImage: EMAIL_IMAGE_PLACEHOLDER_LIFESTYLE,
  socialGuestImage: EMAIL_IMAGE_PLACEHOLDER_AVATAR,
  exp1Image: EMAIL_IMAGE_PLACEHOLDER_SQUARE,
  exp2Image: EMAIL_IMAGE_PLACEHOLDER_SQUARE,
  exp3Image: EMAIL_IMAGE_PLACEHOLDER_SQUARE
}

export const EMAIL_IMAGE_FIELD_KEYS = Object.keys(EMAIL_IMAGE_FIELD_PLACEHOLDERS)
export const LOCAL_PRESERVE_IMAGE_FIELD_KEYS = EMAIL_IMAGE_FIELD_KEYS.filter(key => key !== 'logoUrl')

function hasImageValue(value) {
  return typeof value === 'string' ? value.trim().length > 0 : value != null && value !== ''
}

export function isPlaceholderImageUrl(value) {
  return `${value || ''}`.includes(PLACEHOLDER_BASE)
}

export function resolveEditorLogoUrl(variables = {}, brandLogoUrl = '') {
  const normalized = normalizeEmailImageFieldKeys(variables)
  const explicitLogo = `${normalized.logoUrl || ''}`.trim()
  const brandLogo = `${brandLogoUrl || ''}`.trim()

  if (explicitLogo && !isPlaceholderImageUrl(explicitLogo)) {
    return explicitLogo
  }

  return brandLogo || explicitLogo
}

export function normalizeEmailImageFieldKeys(variables = {}) {
  const next = { ...variables }

  EMAIL_IMAGE_FIELD_KEYS.forEach(key => {
    if (hasImageValue(next[key])) {
      return
    }

    const pascalKey = key.charAt(0).toUpperCase() + key.slice(1)
    if (hasImageValue(next[pascalKey])) {
      next[key] = next[pascalKey]
    }
  })

  return next
}

export function preserveLocalEmailImageFields(prevState = {}, nextState = {}) {
  const merged = { ...nextState }

  LOCAL_PRESERVE_IMAGE_FIELD_KEYS.forEach(key => {
    const localValue = `${prevState[key] || ''}`.trim()
    const remoteValue = `${merged[key] || ''}`.trim()

    if (localValue && !isPlaceholderImageUrl(localValue) && localValue !== remoteValue) {
      merged[key] = prevState[key]
    }
  })

  return merged
}

export function applyUserEditedImageFields(emailState = {}, userImageOverrides = {}) {
  const next = { ...normalizeEmailImageFieldKeys(emailState) }

  LOCAL_PRESERVE_IMAGE_FIELD_KEYS.forEach(key => {
    const override = `${userImageOverrides[key] || ''}`.trim()

    if (override && !isPlaceholderImageUrl(override)) {
      next[key] = userImageOverrides[key]
    }
  })

  return next
}

// Picking a layout hands the user a fresh template, so the content image slots go back to
// their placeholder boxes instead of carrying artwork cropped for the previous layout. The
// logo is left alone because it belongs to the hotel, not to the layout.
export function clearEmailContentImageFields(emailState = {}) {
  const next = { ...emailState }

  LOCAL_PRESERVE_IMAGE_FIELD_KEYS.forEach(key => {
    next[key] = ''
  })

  return next
}

export function resolveEditorImageField(emailState = {}, property, userImageOverrides = {}) {
  const override = `${userImageOverrides[property] || ''}`.trim()

  if (override && !isPlaceholderImageUrl(override)) {
    return override
  }

  return `${normalizeEmailImageFieldKeys(emailState)[property] || ''}`.trim()
}

export function buildEditorTemplateVariables(
  emailState = {},
  { brandLogoUrl = '', userImageOverrides = {}, socialUrls = {} } = {}
) {
  const withImages = applyUserEditedImageFields(emailState, userImageOverrides)

  return normalizeEmailImageFieldKeys({
    ...withImages,
    logoUrl: resolveEditorLogoUrl(withImages, brandLogoUrl),
    ...socialUrls
  })
}

export function mergeLoadedEmailState(
  prevState = {},
  nextState = {},
  brandLogoUrl = '',
  userImageOverrides = {}
) {
  // Prefer remote/next state so applying a saved design is not blocked by the
  // previous in-memory template defaults or campaign images.
  const merged = { ...nextState }

  LOCAL_PRESERVE_IMAGE_FIELD_KEYS.forEach(key => {
    const override = `${userImageOverrides[key] || ''}`.trim()

    if (override && !isPlaceholderImageUrl(override)) {
      merged[key] = userImageOverrides[key]
    }
  })

  const logoUrl = resolveEditorLogoUrl(merged, brandLogoUrl)

  return logoUrl ? { ...merged, logoUrl } : merged
}

export function applyEmailImagePlaceholders(variables = {}) {
  const next = { ...variables }

  EMAIL_IMAGE_FIELD_KEYS.forEach(key => {
    if (!hasImageValue(next[key])) {
      next[key] = EMAIL_IMAGE_FIELD_PLACEHOLDERS[key]
    }
  })

  return next
}
